import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronRight, ChevronDown, AlertTriangle, CheckCircle2, XCircle, Clock, Zap, Activity, Database, Cpu, Search, TrendingUp, TrendingDown, AlertCircle, FileText, Copy, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TreeView } from '@/components/ui/tree-view';
import { getNodeDefinition } from '@/utils/nodeDefinitions';
import { NodeIcon } from '@/components/ui/node-icon';
import { executionsApi } from '@/lib/api';
import { transformBackendLogs } from '@/utils/logTransform';
import { toast } from 'sonner';

interface NodeExecution {
  id: string;
  node_id: string;
  node_label: string;
  node_type: string;
  execution_order: number;
  status: 'success' | 'error' | 'running' | 'pending' | 'skipped';
  started_at?: number;
  completed_at?: number;
  duration_ms?: number;
  input_data?: string;
  output_data?: string;
  error?: string;
  logs?: Array<{ timestamp: number; message: string; level: string }>;
  memory_used_bytes?: number;
  cpu_time_ms?: number;
  is_loop_iteration?: boolean;
  loop_iteration_index?: number;
  parent_node_id?: string;
  // Sub-node (tool execution) tracking
  is_sub_node?: boolean;
  parent_node_execution_id?: string;
  tool_name?: string;
}

interface ExecutionMetrics {
  execution_id: string;
  execution_time_ms: number;
  total_time_ms: number;
  slowest_node_id: string;
  slowest_node_label: string;
  slowest_node_duration_ms: number;
  fastest_node_id: string;
  fastest_node_label: string;
  fastest_node_duration_ms: number;
  avg_node_duration_ms: number;
  median_node_duration_ms: number;
  peak_memory_bytes: number;
  total_memory_bytes: number;
  avg_memory_bytes: number;
  total_cpu_time_ms: number;
  total_loop_iterations: number;
  failed_loop_iterations: number;
  bottleneck_node_id: string;
  bottleneck_node_label: string;
  bottleneck_reason: string;
}

interface LoopIteration {
  id: string;
  loop_node_id: string;
  loop_node_label: string;
  iteration_index: number;
  status: string;
  started_at: number;
  completed_at: number;
  duration_ms: number;
  item_value: string;
  output_data: string;
  error: string;
}

interface ExecutionModalV2Props {
  execution: {
    id: string;
    status: 'completed' | 'failed' | 'running' | 'cancelled';
    started_at: number;
    completed_at?: number;
    duration_ms?: number;
    workflow_name?: string;
    error?: string;
  };
  nodes: NodeExecution[];
  onClose: () => void;
}

export const ExecutionModalV2: React.FC<ExecutionModalV2Props> = ({
  execution: initialExecution,
  nodes: initialNodes,
  onClose,
}) => {
  const [execution, setExecution] = useState(initialExecution);
  const [nodes, setNodes] = useState(initialNodes);
  const [selectedNode, setSelectedNode] = useState<NodeExecution | null>(initialNodes[0] || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'performance' | 'logs' | 'metrics'>('details');
  const [loopIterations, setLoopIterations] = useState<LoopIteration[]>([]);
  
  // Lazy loading states
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [nodesLoaded, setNodesLoaded] = useState(initialNodes.length > 0);

  // Cache for full node data (keyed by node ID)
  const [fullDataCache, setFullDataCache] = useState<Record<string, NodeExecution>>({});
  const [loadingFullData, setLoadingFullData] = useState(false);

  // Get full data for selected node from cache
  const selectedNodeFullData = selectedNode ? fullDataCache[selectedNode.id] : null;

  // Collapsible section states
  const [inputOpen, setInputOpen] = useState(true);
  const [outputOpen, setOutputOpen] = useState(true);

  // Fetch execution data and node executions (lazy loaded)
  const fetchExecutionData = async () => {
    if (nodesLoaded) return; // Already loaded
    
    try {
      setLoadingNodes(true);
      
      // Fetch execution details
      const execResponse = await executionsApi.get(execution.id);
      const execData = execResponse.data;
      
      setExecution({
        id: execData.id,
        status: execData.status,
        started_at: execData.started_at || execData.created_at,
        completed_at: execData.completed_at,
        duration_ms: execData.duration_ms,
        workflow_name: execData.workflow_name,
        error: execData.error,
      });

      // Fetch node executions (lightweight for initial load)
      const nodesResponse = await executionsApi.getNodeExecutions(execution.id);
      // Handle both paginated response { items: [...] } and legacy array response
      const nodesData = nodesResponse.data?.items ?? nodesResponse.data ?? [];

      setNodes(Array.isArray(nodesData) ? nodesData : []);
      setNodesLoaded(true);
      
      // Select first node if none selected and fetch its full data
      if (!selectedNode && nodesData.length > 0) {
        setSelectedNode(nodesData[0]);
        // Fetch full data for first node
        fetchNodeFullData(nodesData[0].id);
      }
    } catch (error) {
      // console.error('Failed to fetch execution data:', error);
    } finally {
      setLoadingNodes(false);
    }
  };

  // Fetch full data for a specific node (with complete input/output data)
  const fetchNodeFullData = async (nodeId: string) => {
    // Skip if already cached
    if (fullDataCache[nodeId]) {
      return;
    }

    try {
      setLoadingFullData(true);
      // Fetch only this specific node's full data by its database ID
      const response = await executionsApi.getNodeExecutionById(execution.id, nodeId);
      const fullNode = response.data;
      if (fullNode) {
        // Add to cache
        setFullDataCache(prev => ({ ...prev, [nodeId]: fullNode }));
      }
    } catch (error) {
      console.error('Failed to fetch full node data:', error);
    } finally {
      setLoadingFullData(false);
    }
  };

  // Fetch data on mount - only if not already provided
  useEffect(() => {
    if (!nodesLoaded) {
      fetchExecutionData();
    }
  }, []);

  // Fetch full data for initially selected node
  useEffect(() => {
    if (selectedNode && !selectedNodeFullData && !loadingFullData) {
      fetchNodeFullData(selectedNode.id);
    }
  }, [selectedNode?.id]);

  // Poll for updates when execution is running
  useEffect(() => {
    if (execution.status !== 'running' || !nodesLoaded) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        // Lightweight query for polling - just status updates
        const nodesResponse = await executionsApi.getNodeExecutions(execution.id);
        // Handle both paginated response { items: [...] } and legacy array response
        const nodesData = nodesResponse.data?.items ?? nodesResponse.data ?? [];

        setNodes(Array.isArray(nodesData) ? nodesData : []);
        
        // Update selected node if it exists in the new data
        if (selectedNode) {
          const updatedNode = nodesData.find((n: NodeExecution) => n.id === selectedNode.id);
          if (updatedNode) {
            setSelectedNode(updatedNode);
          }
        }
      } catch (error) {
        // Silent fail for polling
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [execution.status, nodesLoaded]);

  // Calculate stats
  const stats = useMemo(() => {
    const successCount = nodes.filter(n => n.status === 'success').length;
    const errorCount = nodes.filter(n => n.status === 'error').length;
    const totalMemory = nodes.reduce((sum, n) => sum + (n.memory_used_bytes || 0), 0);
    const totalCpu = nodes.reduce((sum, n) => sum + (n.cpu_time_ms || 0), 0);

    return {
      successCount,
      errorCount,
      totalMemory,
      totalCpu,
    };
  }, [nodes]);

  // Calculate comprehensive metrics from nodes data
  const calculatedMetrics = useMemo((): ExecutionMetrics | null => {
    if (nodes.length === 0) return null;

    const nodesWithDuration = nodes.filter(n => n.duration_ms && n.duration_ms > 0);
    if (nodesWithDuration.length === 0) return null;

    // Sort by duration
    const sortedByDuration = [...nodesWithDuration].sort((a, b) => (b.duration_ms || 0) - (a.duration_ms || 0));
    const slowest = sortedByDuration[0];
    const fastest = sortedByDuration[sortedByDuration.length - 1];

    // Calculate durations
    const durations = nodesWithDuration.map(n => n.duration_ms || 0);
    const totalTime = execution.duration_ms || durations.reduce((sum, d) => sum + d, 0);
    const avgDuration = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    const sortedDurations = [...durations].sort((a, b) => a - b);
    const medianDuration = sortedDurations[Math.floor(sortedDurations.length / 2)];

    // Calculate memory
    const nodesWithMemory = nodes.filter(n => n.memory_used_bytes && n.memory_used_bytes > 0);
    const memoryValues = nodesWithMemory.map(n => n.memory_used_bytes || 0);
    const peakMemory = memoryValues.length > 0 ? Math.max(...memoryValues) : 0;
    const totalMemory = memoryValues.reduce((sum, m) => sum + m, 0);
    const avgMemory = memoryValues.length > 0 ? totalMemory / memoryValues.length : 0;

    // Calculate CPU
    const totalCpu = nodes.reduce((sum, n) => sum + (n.cpu_time_ms || 0), 0);

    // Calculate loop iterations
    const loopIterations = nodes.filter(n => n.is_loop_iteration).length;
    const failedLoopIterations = nodes.filter(n => n.is_loop_iteration && n.status === 'error').length;

    // Detect bottleneck (node consuming >30% of total time)
    const bottleneckPercentage = (slowest.duration_ms || 0) / totalTime * 100;
    const hasBottleneck = bottleneckPercentage > 30;

    return {
      execution_id: execution.id,
      execution_time_ms: execution.duration_ms || 0,
      total_time_ms: totalTime,
      slowest_node_id: slowest.node_id,
      slowest_node_label: slowest.node_label,
      slowest_node_duration_ms: slowest.duration_ms || 0,
      fastest_node_id: fastest.node_id,
      fastest_node_label: fastest.node_label,
      fastest_node_duration_ms: fastest.duration_ms || 0,
      avg_node_duration_ms: avgDuration,
      median_node_duration_ms: medianDuration,
      peak_memory_bytes: peakMemory,
      total_memory_bytes: totalMemory,
      avg_memory_bytes: avgMemory,
      total_cpu_time_ms: totalCpu,
      total_loop_iterations: loopIterations,
      failed_loop_iterations: failedLoopIterations,
      bottleneck_node_id: hasBottleneck ? slowest.node_id : '',
      bottleneck_node_label: hasBottleneck ? slowest.node_label : '',
      bottleneck_reason: hasBottleneck ? `Consuming ${bottleneckPercentage.toFixed(0)}% of total execution time` : '',
    };
  }, [nodes, execution]);

  // Filter nodes by search
  const filteredNodes = useMemo(() => {
    if (!searchQuery) return nodes;
    const query = searchQuery.toLowerCase();
    return nodes.filter(
      n =>
        n.node_label?.toLowerCase().includes(query) ||
        n.node_type?.toLowerCase().includes(query)
    );
  }, [nodes, searchQuery]);

  // Find top 5 slowest nodes for performance bar
  const topSlowNodes = useMemo(() => {
    return [...nodes]
      .filter(n => n.duration_ms && n.duration_ms > 0)
      .sort((a, b) => (b.duration_ms || 0) - (a.duration_ms || 0))
      .slice(0, 5);
  }, [nodes]);

  const maxDuration = topSlowNodes[0]?.duration_ms || 1;

  // Calculate bottleneck
  const bottleneck = useMemo(() => {
    if (!execution.duration_ms) return null;
    const slowest = topSlowNodes[0];
    if (!slowest) return null;
    const percentage = ((slowest.duration_ms || 0) / execution.duration_ms) * 100;
    if (percentage > 30) {
      return { node: slowest, percentage: percentage.toFixed(0) };
    }
    return null;
  }, [topSlowNodes, execution.duration_ms]);

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // Parse JSON string to object for TreeView
  const parseJsonData = (rawData: string | undefined): any => {
    if (!rawData) return null;
    try {
      return typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
    } catch {
      return rawData; // Return as-is if not valid JSON
    }
  };

  // Sanitize data for display by truncating large strings
  const sanitizeForDisplay = (obj: any, maxStringLength = 500): any => {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
      if (obj.length > maxStringLength) {
        const byteSize = new Blob([obj]).size;
        const formatSize = (bytes: number) => {
          if (bytes < 1024) return `${bytes} B`;
          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
          return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        };
        // Check if it looks like base64 (data URL or long alphanumeric string)
        const isBase64 = /^data:[^;]+;base64,/.test(obj) ||
          (obj.length > 1000 && /^[A-Za-z0-9+/=]+$/.test(obj.substring(0, 1000)));
        if (isBase64) {
          const estimatedBinarySize = Math.floor(byteSize * 0.75);
          return `[Binary content: ~${formatSize(estimatedBinarySize)} encoded as ${formatSize(byteSize)} base64]`;
        }
        // For text content, show preview
        const preview = obj.substring(0, maxStringLength).replace(/\n/g, '\\n');
        return `${preview}... [${formatSize(byteSize)} total]`;
      }
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForDisplay(item, maxStringLength));
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = sanitizeForDisplay(value, maxStringLength);
      }
      return result;
    }

    return obj;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <Activity className="w-4 h-4 text-blue-500 animate-pulse" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'error':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'running':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const handleNodeSelect = async (node: NodeExecution) => {
    setSelectedNode(node);
    // Fetch full data for this node (will skip if already cached)
    await fetchNodeFullData(node.id);
  };

  const handlePrevious = async () => {
    if (!selectedNode) return;
    const currentIndex = nodes.findIndex(n => n.id === selectedNode.id);
    if (currentIndex > 0) {
      const prevNode = nodes[currentIndex - 1];
      setSelectedNode(prevNode);
      await fetchNodeFullData(prevNode.id);
    }
  };

  const handleNext = async () => {
    if (!selectedNode) return;
    const currentIndex = nodes.findIndex(n => n.id === selectedNode.id);
    if (currentIndex < nodes.length - 1) {
      const nextNode = nodes[currentIndex + 1];
      setSelectedNode(nextNode);
      await fetchNodeFullData(nextNode.id);
    }
  };

  const currentNodeIndex = selectedNode ? nodes.findIndex(n => n.id === selectedNode.id) : -1;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border rounded-lg shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-xl font-semibold whitespace-nowrap">
                Execution #
              </h2>
              <code className="text-sm font-mono bg-muted px-2 py-1 rounded truncate max-w-md" title={execution.id}>
                {execution.id}
              </code>
            </div>
            <Badge className={getStatusColor(execution.status)}>
              {execution.status}
            </Badge>
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {formatDuration(execution.duration_ms)}
            </span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Execution Summary Bar */}
        <div className="px-4 py-2 border-b bg-muted/30 flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-muted-foreground">{stats.successCount} succeeded</span>
            </div>
            {stats.errorCount > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-muted-foreground">{stats.errorCount} failed</span>
              </div>
            )}
            <div className="h-4 w-px bg-border" />
            <span className="text-muted-foreground">{nodes.length} total nodes</span>
          </div>
          {/* Execution Error Banner */}
          {execution.status === 'failed' && execution.error && (
            <div className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-red-500 font-medium text-xs">Execution Failed: </span>
                <span className="text-red-400 text-xs break-words">{execution.error}</span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content - Split Panel */}
        <div className="flex-1 flex overflow-hidden">
          {loadingNodes ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Activity className="w-8 h-8 animate-pulse text-blue-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading execution details...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Left Panel - Timeline with SCROLL */}
              <div className="w-80 border-r flex flex-col bg-muted/20">
                <div className="p-3 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search nodes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                {/* SCROLLABLE NODE LIST */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-2 space-y-1">
                    {filteredNodes.map((node) => {
                      const isSubNode = node.is_sub_node === true;
                      const nodeDef = getNodeDefinition(node.node_type);

                      // For sub-nodes, the tool node type is stored in node_type
                      // This allows us to get the proper icon for the specific tool
                      const hasToolIcon = isSubNode && nodeDef && nodeDef.icon && node.node_type !== 'tool';

                      // Generate a friendly display name for tools
                      const getToolDisplayName = () => {
                        if (node.node_label && !node.node_label.includes('_')) {
                          return node.node_label;
                        }
                        if (node.tool_name) {
                          const cleanName = node.tool_name.replace(/_[A-Za-z0-9]{10,}$/, '');
                          return cleanName
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ');
                        }
                        // For sub-nodes, node_type contains the tool type
                        if (isSubNode && node.node_type && node.node_type !== 'tool') {
                          return node.node_type
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/-/g, ' ')
                            .trim()
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ');
                        }
                        return 'Tool';
                      };

                      // Sub-node (tool execution) rendering with indentation
                      if (isSubNode) {
                        return (
                          <button
                            key={node.id}
                            onClick={() => handleNodeSelect(node)}
                            className={cn(
                              'w-full text-left py-1.5 px-2 rounded-md transition-all hover:bg-accent/50',
                              'ml-4 border-l-2 border-muted-foreground/20',
                              selectedNode?.id === node.id && 'bg-accent/50 border-l-primary'
                            )}
                          >
                            <div className="flex items-center gap-1.5">
                              {getStatusIcon(node.status)}
                              <div
                                className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                                style={{ backgroundColor: hasToolIcon ? nodeDef.bgColor : 'var(--accent-purple-100)' }}
                              >
                                {hasToolIcon ? (
                                  <NodeIcon
                                    icon={nodeDef.icon}
                                    iconType={nodeDef.iconType}
                                    iconColor={nodeDef.iconColor}
                                    color={nodeDef.color}
                                    className="w-2.5 h-2.5"
                                  />
                                ) : (
                                  <Wrench className="w-2.5 h-2.5 text-accent-purple-600 dark:text-accent-purple-400" />
                                )}
                              </div>
                              <span className="font-medium text-xs flex-1 truncate text-muted-foreground">
                                {getToolDisplayName()}
                              </span>
                              <span className="text-[10px] text-muted-foreground/70 flex-shrink-0">
                                {formatDuration(node.duration_ms)}
                              </span>
                            </div>
                          </button>
                        );
                      }

                      // Regular node rendering
                      return (
                        <button
                          key={node.id}
                          onClick={() => handleNodeSelect(node)}
                          className={cn(
                            'w-full text-left p-2 rounded-lg transition-all hover:bg-accent',
                            selectedNode?.id === node.id && 'bg-accent border border-primary'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {getStatusIcon(node.status)}
                            <div
                              className="w-6 h-6 rounded flex items-center justify-center shrink-0"
                              style={{ backgroundColor: nodeDef.bgColor }}
                            >
                              <NodeIcon
                                icon={nodeDef.icon}
                                iconType={nodeDef.iconType}
                                iconColor={nodeDef.iconColor}
                                color={nodeDef.color}
                                className="w-3 h-3"
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              #{node.execution_order}
                            </span>
                            <span className="font-medium text-sm flex-1 truncate">
                              {node.node_label || node.node_type}
                            </span>
                            {node.id === bottleneck?.node.id && (
                              <AlertTriangle className="w-3 h-3 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground pl-8">
                            <span>{formatDuration(node.duration_ms)}</span>
                          </div>
                        </button>
                      );
                    })}
              </div>
            </div>

            <div className="p-2 border-t text-xs text-muted-foreground text-center">
              Node {currentNodeIndex + 1} of {nodes.length}
            </div>
          </div>

          {/* Right Panel - Details with SCROLL */}
          <div className="flex-1 flex flex-col">
            {/* Tab Navigation */}
            <div className="border-b">
              <div className="flex items-center px-4">
                <button
                  onClick={() => setActiveTab('details')}
                  className={cn(
                    'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === 'details'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  )}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('metrics')}
                  className={cn(
                    'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === 'metrics'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  )}
                >
                  Metrics
                </button>
                <button
                  onClick={() => setActiveTab('logs')}
                  className={cn(
                    'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === 'logs'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  )}
                >
                  Logs
                  {selectedNode?.logs && selectedNode.logs.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {selectedNode.logs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('performance')}
                  className={cn(
                    'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === 'performance'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  )}
                >
                  Performance
                </button>
              </div>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'details' && selectedNode && (
                <div className="p-6 space-y-6">
                  {/* Node Header */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(selectedNode.status)}
                      {(() => {
                        const nodeDef = getNodeDefinition(selectedNode.node_type);
                        return (
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: nodeDef.bgColor }}
                          >
                            <NodeIcon
                              icon={nodeDef.icon}
                              iconType={nodeDef.iconType}
                              iconColor={nodeDef.iconColor}
                              color={nodeDef.color}
                              className="w-4 h-4"
                            />
                          </div>
                        );
                      })()}
                      <h3 className="text-lg font-semibold">
                        #{selectedNode.execution_order} {selectedNode.node_label || selectedNode.node_type}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Input and output data for this node
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(selectedNode.duration_ms)}
                      </span>
                      {selectedNode.started_at && (
                        <span>
                          Started: {new Date(selectedNode.started_at).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Input Data */}
                  {(() => {
                    // Show section if we have data or might have data loading
                    const hasInputData = selectedNodeFullData?.input_data || selectedNode.input_data;
                    if (!hasInputData && !loadingFullData) return null;

                    return (
                      <Collapsible open={inputOpen} onOpenChange={setInputOpen}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                          <span className="font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Input
                            {!selectedNodeFullData && <Activity className="w-3 h-3 animate-spin text-muted-foreground" />}
                          </span>
                          {inputOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="bg-muted/30 rounded max-h-80 overflow-auto border p-2">
                            {(() => {
                              // Show loading until we have full data (not truncated)
                              if (!selectedNodeFullData) {
                                return (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 px-2">
                                    <Activity className="w-4 h-4 animate-spin" />
                                    Loading input data...
                                  </div>
                                );
                              }

                              const inputData = selectedNodeFullData.input_data;
                              if (!inputData) {
                                return (
                                  <div className="text-sm text-muted-foreground py-2 px-2">
                                    No input data
                                  </div>
                                );
                              }

                              const parsed = parseJsonData(inputData);
                              // If parsing returned same string (failed), show as raw text
                              if (typeof parsed === 'string') {
                                return (
                                  <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-wrap-anywhere">
                                    {parsed}
                                  </pre>
                                );
                              }

                              // Sanitize data to truncate large strings for TreeView display
                              const sanitized = sanitizeForDisplay(parsed);
                              const isLargeData = inputData.length > 100000;

                              return (
                                <div className="space-y-2">
                                  {isLargeData && (
                                    <div className="flex items-center justify-between text-xs text-muted-foreground pb-1 border-b">
                                      <span>Large data ({(inputData.length / 1024).toFixed(1)} KB) - large strings truncated</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-xs"
                                        onClick={() => {
                                          navigator.clipboard.writeText(inputData);
                                          toast.success('Copied full data to clipboard');
                                        }}
                                      >
                                        <Copy className="w-3 h-3 mr-1" /> Copy Full
                                      </Button>
                                    </div>
                                  )}
                                  <TreeView
                                    data={sanitized}
                                    defaultExpanded={2}
                                    showTypes={true}
                                    showCopyButtons={true}
                                    maxDepth={5}
                                    className="text-sm"
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })()}

                  {/* Output Data */}
                  {(() => {
                    // Show section if we have data or might have data loading
                    const hasOutputData = selectedNodeFullData?.output_data || selectedNode.output_data;
                    if (!hasOutputData && !loadingFullData) return null;

                    return (
                      <Collapsible open={outputOpen} onOpenChange={setOutputOpen}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                          <span className="font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Output
                            {!selectedNodeFullData && <Activity className="w-3 h-3 animate-spin text-muted-foreground" />}
                          </span>
                          {outputOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="bg-muted/30 rounded max-h-80 overflow-auto border p-2">
                            {(() => {
                              // Show loading until we have full data (not truncated)
                              if (!selectedNodeFullData) {
                                return (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 px-2">
                                    <Activity className="w-4 h-4 animate-spin" />
                                    Loading output data...
                                  </div>
                                );
                              }

                              const outputData = selectedNodeFullData.output_data;
                              if (!outputData) {
                                return (
                                  <div className="text-sm text-muted-foreground py-2 px-2">
                                    No output data
                                  </div>
                                );
                              }

                              const parsed = parseJsonData(outputData);
                              // If parsing returned same string (failed), show as raw text
                              if (typeof parsed === 'string') {
                                return (
                                  <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-wrap-anywhere">
                                    {parsed}
                                  </pre>
                                );
                              }

                              // Sanitize data to truncate large strings for TreeView display
                              const sanitized = sanitizeForDisplay(parsed);
                              const isLargeData = outputData.length > 100000;

                              return (
                                <div className="space-y-2">
                                  {isLargeData && (
                                    <div className="flex items-center justify-between text-xs text-muted-foreground pb-1 border-b">
                                      <span>Large data ({(outputData.length / 1024).toFixed(1)} KB) - large strings truncated</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-xs"
                                        onClick={() => {
                                          navigator.clipboard.writeText(outputData);
                                          toast.success('Copied full data to clipboard');
                                        }}
                                      >
                                        <Copy className="w-3 h-3 mr-1" /> Copy Full
                                      </Button>
                                    </div>
                                  )}
                                  <TreeView
                                    data={sanitized}
                                    defaultExpanded={2}
                                    showTypes={true}
                                    showCopyButtons={true}
                                    maxDepth={5}
                                    className="text-sm"
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })()}

                  {/* Error */}
                  {selectedNode.error && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-red-500">
                        <XCircle className="w-4 h-4" />
                        Error
                      </h4>
                      <div className="bg-red-500/10 border border-red-500/20 rounded max-h-60 overflow-auto">
                        <pre className="p-3 text-xs font-mono text-red-500 whitespace-pre-wrap break-all">
                          {selectedNode.error}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'metrics' && selectedNode && (
                <div className="p-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      {(() => {
                        const nodeDef = getNodeDefinition(selectedNode.node_type);
                        return (
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: nodeDef.bgColor }}
                          >
                            <NodeIcon
                              icon={nodeDef.icon}
                              iconType={nodeDef.iconType}
                              iconColor={nodeDef.iconColor}
                              color={nodeDef.color}
                              className="w-4 h-4"
                            />
                          </div>
                        );
                      })()}
                      <h3 className="text-lg font-semibold">Node Metrics</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Performance metrics for {selectedNode.node_label || selectedNode.node_type}
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Execution Time */}
                    <div className="bg-muted/50 border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-5 h-5 text-primary" />
                        <h4 className="font-semibold">Execution Time</h4>
                      </div>
                      <div className="text-3xl font-bold text-primary">
                        {formatDuration(selectedNode.duration_ms)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Started: {selectedNode.started_at ? new Date(selectedNode.started_at).toLocaleString() : 'N/A'}
                      </div>
                      {selectedNode.completed_at && (
                        <div className="text-xs text-muted-foreground">
                          Completed: {new Date(selectedNode.completed_at).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {/* Resource Usage */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        Resource Usage
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-muted/50 border rounded-lg p-4">
                          <div className="text-xs text-muted-foreground mb-2">Memory Used</div>
                          <div className="text-2xl font-bold">
                            {formatBytes(selectedNode.memory_used_bytes)}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {selectedNode.memory_used_bytes ? `${(selectedNode.memory_used_bytes / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                          </div>
                        </div>
                        <div className="bg-muted/50 border rounded-lg p-4">
                          <div className="text-xs text-muted-foreground mb-2">CPU Time</div>
                          <div className="text-2xl font-bold">
                            {formatDuration(selectedNode.cpu_time_ms)}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {selectedNode.cpu_time_ms ? `${selectedNode.cpu_time_ms.toFixed(2)} ms` : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Node Information */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        Node Information
                      </h4>
                      <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Node Type</span>
                          <Badge variant="outline">{selectedNode.node_type}</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Execution Order</span>
                          <span className="font-mono font-semibold">#{selectedNode.execution_order}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Status</span>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(selectedNode.status)}
                            <span className="font-semibold capitalize">{selectedNode.status}</span>
                          </div>
                        </div>
                        {selectedNode.node_id && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Node ID</span>
                            <span className="font-mono text-xs">{selectedNode.node_id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'logs' && selectedNode && (
                <div className="p-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      {(() => {
                        const nodeDef = getNodeDefinition(selectedNode.node_type);
                        return (
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: nodeDef.bgColor }}
                          >
                            <NodeIcon
                              icon={nodeDef.icon}
                              iconType={nodeDef.iconType}
                              iconColor={nodeDef.iconColor}
                              color={nodeDef.color}
                              className="w-4 h-4"
                            />
                          </div>
                        );
                      })()}
                      <h3 className="text-lg font-semibold">Execution Logs</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Logs for {selectedNode.node_label || selectedNode.node_type}
                    </p>
                  </div>
                  
                  {(() => {
                    // Backend now returns logs in the correct format
                    const logs = Array.isArray(selectedNode.logs) ? selectedNode.logs : [];
                    return logs.length > 0 ? (
                      <div className="bg-muted/50 border rounded-lg p-4 max-h-[600px] overflow-y-auto">
                        <div className="space-y-2 font-mono text-xs">
                          {logs.map((log: any, i: number) => (
                            <div key={i} className="py-1 hover:bg-muted/50 px-2 rounded">
                              <div className="grid grid-cols-[auto_auto_1fr] gap-3 items-start">
                                <span className="text-muted-foreground">
                                  {new Date(log.timestamp).toLocaleTimeString()}
                                </span>
                                <span className={cn(
                                  'font-semibold uppercase text-[10px] mt-0.5',
                                  log.level === 'error' && 'text-red-500',
                                  log.level === 'warn' && 'text-yellow-500',
                                  log.level === 'info' && 'text-blue-500',
                                  !log.level && 'text-muted-foreground'
                                )}>
                                  [{log.level || 'log'}]
                                </span>
                                <div className="break-words whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                  {log.message}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No logs available for this node</p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeTab === 'performance' && (
                <div className="p-6 space-y-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-1">Execution Performance</h3>
                    <p className="text-sm text-muted-foreground">
                      Overall performance analysis for the entire execution
                    </p>
                  </div>
                  {calculatedMetrics ? (
                    <>
                      {/* Bottleneck Alert */}
                      {calculatedMetrics.bottleneck_node_id && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-red-500 font-semibold mb-2">
                            <AlertTriangle className="w-5 h-5" />
                            BOTTLENECK DETECTED
                          </div>
                          <p className="text-sm">
                            <strong>{calculatedMetrics.bottleneck_node_label}</strong> is consuming{' '}
                            {((calculatedMetrics.slowest_node_duration_ms / calculatedMetrics.total_time_ms) * 100).toFixed(0)}%
                            of total execution time
                          </p>
                          {calculatedMetrics.bottleneck_reason && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {calculatedMetrics.bottleneck_reason}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Key Metrics */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-green-500 mb-2">
                              <TrendingUp className="w-4 h-4" />
                              <span className="text-sm font-semibold">Fastest Node</span>
                            </div>
                            <div className="text-2xl font-bold">
                              {formatDuration(calculatedMetrics.fastest_node_duration_ms)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {calculatedMetrics.fastest_node_label}
                            </div>
                          </div>

                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-red-500 mb-2">
                              <TrendingDown className="w-4 h-4" />
                              <span className="text-sm font-semibold">Slowest Node</span>
                            </div>
                            <div className="text-2xl font-bold">
                              {formatDuration(calculatedMetrics.slowest_node_duration_ms)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {calculatedMetrics.slowest_node_label}
                            </div>
                          </div>

                          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-blue-500 mb-2">
                              <Database className="w-4 h-4" />
                              <span className="text-sm font-semibold">Peak Memory</span>
                            </div>
                            <div className="text-2xl font-bold">
                              {formatBytes(calculatedMetrics.peak_memory_bytes)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Avg: {formatBytes(calculatedMetrics.avg_memory_bytes)}
                            </div>
                          </div>

                          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-purple-500 mb-2">
                              <Cpu className="w-4 h-4" />
                              <span className="text-sm font-semibold">Total CPU Time</span>
                            </div>
                            <div className="text-2xl font-bold">
                              {formatDuration(calculatedMetrics.total_cpu_time_ms)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Across all nodes
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Optimization Recommendations */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-blue-500" />
                          Optimization Recommendations
                        </h3>
                        <div className="space-y-2">
                          {calculatedMetrics.bottleneck_node_id && (
                            <div className="bg-muted p-3 rounded text-sm">
                              • Consider optimizing <strong>{calculatedMetrics.bottleneck_node_label}</strong> to reduce execution time
                            </div>
                          )}
                          {calculatedMetrics.total_loop_iterations > 10 && (
                            <div className="bg-muted p-3 rounded text-sm">
                              • {calculatedMetrics.total_loop_iterations} loop iterations detected - consider batch processing
                            </div>
                          )}
                          {calculatedMetrics.peak_memory_bytes > 500 * 1024 * 1024 && (
                            <div className="bg-muted p-3 rounded text-sm">
                              • High memory usage detected ({formatBytes(calculatedMetrics.peak_memory_bytes)}) - review data processing
                            </div>
                          )}
                          {nodes.length > 20 && (
                            <div className="bg-muted p-3 rounded text-sm">
                              • {nodes.length} nodes in workflow - consider parallelizing independent nodes
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Execution Statistics */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Execution Statistics</h3>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground mb-1">Average Duration</div>
                            <div className="font-semibold">
                              {formatDuration(calculatedMetrics.avg_node_duration_ms)}
                            </div>
                          </div>
                          <div>
                            <div className="text-muted-foreground mb-1">Median Duration</div>
                            <div className="font-semibold">
                              {formatDuration(calculatedMetrics.median_node_duration_ms)}
                            </div>
                          </div>
                          <div>
                            <div className="text-muted-foreground mb-1">Total Loop Iterations</div>
                            <div className="font-semibold">
                              {calculatedMetrics.total_loop_iterations || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No performance metrics available</p>
                      <p className="text-xs mt-2">Metrics are generated after execution completes</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Footer */}
            <div className="p-3 border-t flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={currentNodeIndex <= 0}
              >
                ← Previous Node
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={currentNodeIndex >= nodes.length - 1}
              >
                Next Node →
              </Button>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
